"use client";

import { useState } from "react";
import { FixedSizeList as List } from "react-window";
import toast from "react-hot-toast";
import { callApi } from "../../common/Apicall";
import {
  setSelectedConversation,
  setWithMessagesGroups,
} from "../../Slices/ChatSlice";
import { useDispatch } from "react-redux";
import {
  IoClose,
  IoSearch,
  IoCheckmark,
  IoPeople,
  IoPersonAdd,
  IoInformationCircle,
} from "react-icons/io5";

const CreateGroupModal = ({
  showCreateGroupModal,
  setShowCreateGroupModal,
  currentUser,
  conversationsData,
  Api_URL,
}) => {
  const [groupName, setGroupName] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [participantSearchTerm, setParticipantSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const dispatch = useDispatch();

  const filteredParticipants = conversationsData?.filter(
    (participant) =>
      participant?.label
        ?.toLowerCase()
        ?.includes(participantSearchTerm?.toLowerCase()) ||
      participant?.email
        ?.toLowerCase()
        ?.includes(participantSearchTerm?.toLowerCase())
  );

  const handleGroupNameChange = (event) => {
    setGroupName(event.target.value);
    setErrorMessage("");
  };

  const handleParticipantSelection = (participantId) => {
    const newSelectedParticipants = new Set(selectedParticipants);
    if (newSelectedParticipants.has(participantId)) {
      newSelectedParticipants.delete(participantId);
    } else {
      newSelectedParticipants.add(participantId);
    }
    setSelectedParticipants(newSelectedParticipants);
    setErrorMessage("");
  };

  const handleSelectAll = () => {
    const allParticipantIds = new Set(filteredParticipants.map((p) => p.value));
    setSelectedParticipants(allParticipantIds);
  };

  const handleDeselectAll = () => {
    setSelectedParticipants(new Set());
  };

  const fetchConversations = async () => {
    try {
      const res = await callApi(
        `${Api_URL}/GroupChat/Users-With-GroupMessages`,
        "GET"
      );
      if (res.error) throw new Error(res.error);
      dispatch(setWithMessagesGroups(res));
      dispatch(
        setSelectedConversation({
          conversation: null,
          type: "group",
        })
      );
    } catch (error) {
      toast.error(`Failed to fetch conversations: ${error.message}`);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setErrorMessage("Group name is required.");
      return;
    }
    if (selectedParticipants.size === 0) {
      setErrorMessage("Please select at least one participant.");
      return;
    }
    setIsCreatingGroup(true);

    const finalParticipants = new Set(selectedParticipants);
    finalParticipants.add(currentUser);

    const payload = {
      userIds: Array.from(finalParticipants),
      groupName: groupName,
      createdByUserId: currentUser,
    };
    try {
      const res = await callApi(
        `${Api_URL}/GroupChat/Create-Group`,
        "post",
        payload
      );

      if (res.error) throw new Error(res.error);
      fetchConversations();
      toast.success("Group created successfully!");
      setIsCreatingGroup(false);
      setGroupName("");
      setSelectedParticipants(new Set());
      setShowCreateGroupModal(false);
      setErrorMessage("");
    } catch (error) {
      setIsCreatingGroup(false);
      toast.error(error.message || "Something went wrong!");
    }
  };

  const handleCancel = () => {
    setGroupName("");
    setSelectedParticipants(new Set());
    setShowCreateGroupModal(false);
    setErrorMessage("");
  };

  // Function to generate initials from name
  const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Function to generate a consistent color based on user ID
  const getUserColor = (userId) => {
    const colors = [
      "bg-blue-100 text-blue-800",
      "bg-green-100 text-green-800",
      "bg-purple-100 text-purple-800",
      "bg-yellow-100 text-yellow-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
      "bg-red-100 text-red-800",
      "bg-teal-100 text-teal-800",
    ];

    // Convert userId to string and then create a simple hash
    const userIdStr = String(userId);
    // Use a simpler approach to generate a consistent index
    let hash = 0;
    for (let i = 0; i < userIdStr.length; i++) {
      hash = (hash + userIdStr.charCodeAt(i)) % colors.length;
    }

    return colors[hash];
  };

  const ParticipantRow = ({ index, style }) => {
    const participant = filteredParticipants[index];
    const isSelected = selectedParticipants.has(participant.value);

    return (
      <div
        style={style}
        className={`flex items-center p-3 cursor-pointer transition-colors border-b border-gray-100 ${
          isSelected ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
        onClick={() => handleParticipantSelection(participant.value)}
      >
        <div className="flex items-center w-full">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getUserColor(
              participant.value
            )}`}
          >
            {getInitials(participant.label)}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {participant.label}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {participant.email}
            </p>
          </div>

          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 border ${
              isSelected
                ? "bg-blue-500 border-blue-500 text-white"
                : "border-gray-300 bg-white"
            }`}
          >
            {isSelected && <IoCheckmark className="w-4 h-4" />}
          </div>
        </div>
      </div>
    );
  };

  return (
    showCreateGroupModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
          {/* Header */}
          {/* <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <IoPeople className="text-2xl" />
              Create New Group
            </h2>
            <button
              onClick={handleCancel}
              className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
              aria-label="Close"
            >
              <IoClose className="w-6 h-6" />
            </button>
          </div> */}

          {/* Scrollable content area */}
          <div className="overflow-y-auto flex-grow">
            {/* Group Name Input */}
            <div className="px-6 py-3 border-b border-gray-200">
              <label
                htmlFor="groupName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Group Name
              </label>
              <input
                id="groupName"
                type="text"
                placeholder="Enter a name for your group (Max 30)"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={groupName}
                onChange={handleGroupNameChange}
                maxLength={30}
              />
            </div>

            {/* Participants Section */}
            <div className="px-6 pt-2 pb-2">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                <span>Add Participants</span>
                <span className="text-blue-600 text-xs font-semibold">
                  {selectedParticipants.size} selected
                </span>
              </h3>

              {/* Search input */}
              <div className="relative mb-2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IoSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name or email"
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={participantSearchTerm}
                  onChange={(e) => setParticipantSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Participants list */}
            <div className="">
              {filteredParticipants?.length > 0 ? (
                <div className="border border-gray-200  overflow-hidden">
                  <List
                    height={Math.min(240, filteredParticipants.length * 64)}
                    itemCount={filteredParticipants?.length}
                    itemSize={64}
                    width="100%"
                    className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 min-h-[280px]"
                  >
                    {ParticipantRow}
                  </List>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 px-4 text-center border border-gray-200 rounded-lg">
                  <div className="text-gray-400 mb-2">
                    <IoSearch className="w-10 h-10 mx-auto" />
                  </div>
                  <p className="text-gray-600 font-medium">No users found</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Try a different search term
                  </p>
                </div>
              )}
            </div>

            {/* Error message */}
            {errorMessage && (
              <div className="px-6 py-2 mt-4 bg-red-50 border-t border-red-100 flex items-start">
                <IoInformationCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-red-600 text-sm">{errorMessage}</p>
              </div>
            )}
          </div>

          {/* Footer with action buttons - fixed at the bottom */}
          <div className="px-6 py-2 bg-gray-50 flex justify-between  flex-shrink-0">
            <button
              onClick={handleCancel}
              className="px-4 py-1 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Cancel
            </button>

            <button
              className={`inline-flex items-center px-4 py-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                groupName.trim() &&
                selectedParticipants.size > 0 &&
                !isCreatingGroup
                  ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              onClick={handleCreateGroup}
              disabled={
                !groupName.trim() ||
                selectedParticipants.size === 0 ||
                isCreatingGroup
              }
            >
              {isCreatingGroup ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <IoPersonAdd className="mr-2" />
                  Create Group
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default CreateGroupModal;
